import { Box, Button, Card, CardContent, CardHeader, Typography, Link, Chip, List, ListItem } from '@mui/material';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { TwilioRegLog, TwilioRegistrationRecord } from '../models';
import { TwilioSessionsList } from '../components/TwilioSessionsList';

interface Props {
  twilioRegLog?: TwilioRegLog;
  sessions?: TwilioRegistrationRecord[];
  isLoadingSessions?: boolean;
  onRestartFailedStep?: (logId: string) => void;
  isRestarting?: boolean;
}

export const TwilioRegistrationDetailsPresenter = ({
  twilioRegLog,
  sessions = [],
  isLoadingSessions = false,
  onRestartFailedStep,
  isRestarting = false
}: Props) => {
  const handleRestartClick = () => {
    if (twilioRegLog?.identityId && onRestartFailedStep) {
      onRestartFailedStep(twilioRegLog.identityId);
    }
  };

  const getStatusText = (log?: TwilioRegLog): string => {
    if (!log) return 'Pending';
    if (log.lastStepFailed) return 'Failed';
    if (log.finished) return 'Completed';
    if (log.lastExecutedStepIndex >= 0) return 'In Progress';
    return 'Pending';
  };

  const getStatusColor = (log?: TwilioRegLog): string => {
    if (!log) return 'inherit';
    if (log.lastStepFailed) return 'red';
    if (log.finished && !log.lastStepFailed) return 'green';
    return 'inherit';
  };

  const statusText = getStatusText(twilioRegLog);
  const statusColor = getStatusColor(twilioRegLog);

  const brandUrl = twilioRegLog?.brandId ?
    `${process.env.REACT_APP_TWILIO_BRANDS_URL}/${twilioRegLog.brandId}` :
    undefined;

  const campaignUrl = (twilioRegLog?.brandId && twilioRegLog?.campaignId) ?
    `${process.env.REACT_APP_TWILIO_CAMPAIGNS_URL}/${twilioRegLog.brandId}/connected-campaigns/${twilioRegLog.campaignId}` :
    undefined;

  const sortedSessions = [...sessions].sort((a, b) => {
    if (a.id === twilioRegLog?.identityId && b.id !== twilioRegLog?.identityId) return -1;
    if (a.id !== twilioRegLog?.identityId && b.id === twilioRegLog?.identityId) return 1;
    return 0;
  });

  return (
    <Box sx={styles.container}>
      <Box sx={styles.leftColumn}>
        <Card sx={styles.detailsCard}>
          <CardHeader
            title="Registration Details"
            sx={styles.cardHeader}
          />
          <CardContent sx={styles.cardContent}>
            <Box sx={styles.detailsSection}>
              <Box sx={styles.detailRow}>
                <Typography>
                  Brand ID: {twilioRegLog?.brandId ? (
                    <Typography>
                      <Link href={brandUrl} target="_blank" rel="noopener" sx={styles.boldText}>
                        {twilioRegLog.brandId}
                      </Link>
                    </Typography>
                  ) :
                    <Typography sx={styles.boldText}>{"<No ID>"}</Typography>}
                </Typography>
              </Box>

              <Box sx={styles.detailRow}>
                <Typography>
                  Messaging Service ID: <Typography sx={styles.boldText}>{twilioRegLog?.messagingServiceId || '<No ID>'}</Typography>
                </Typography>
              </Box>

              <Box sx={styles.detailRow}>
                <Typography>
                  Campaign ID: {twilioRegLog?.campaignId ? (
                    <Typography>
                      <Link href={campaignUrl} target="_blank" rel="noopener" sx={styles.boldText}>
                        {twilioRegLog.campaignId}
                      </Link>
                    </Typography>
                  ) :
                    <Typography sx={styles.boldText}>{"<No ID>"}</Typography>}
                </Typography>
              </Box>

              <Box sx={styles.detailRow}>
                <Typography>
                  Current Step: {twilioRegLog?.lastExecutedStepIndex ? twilioRegLog.lastExecutedStepIndex + 1 : 'N/A'}
                </Typography>
                {twilioRegLog?.currentStepDescription && (
                  <Typography sx={{ ...styles.stepDescription, fontWeight: 'bold' }}>
                    {twilioRegLog.currentStepDescription}
                  </Typography>
                )}
              </Box>

              <Box sx={styles.detailRow}>
                <Typography>
                  Status: <span style={{ color: statusColor }}>
                    {statusText}
                  </span>
                </Typography>
              </Box>

              {twilioRegLog?.lastStepErr && (
                <Box sx={styles.detailRow}>
                  <Typography>
                    Error Message: <span style={styles.errorText}>{twilioRegLog.lastStepErr}</span>
                  </Typography>
                </Box>
              )}

              {twilioRegLog?.lastStepFailed && onRestartFailedStep && (
                <Box sx={styles.buttonContainer}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<RestartAltIcon />}
                    onClick={handleRestartClick}
                    disabled={isRestarting}
                  >
                    {isRestarting ? 'Restarting...' : 'Restart Failed Step'}
                  </Button>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        <Card sx={styles.sessionsCard}>
          <CardHeader
            title="Active Sessions"
            sx={styles.cardHeader}
          />
          <CardContent sx={styles.cardContent}>
            <TwilioSessionsList
              sessions={sortedSessions}
              isLoading={isLoadingSessions}
              currentUserId={twilioRegLog?.identityId}
            />
          </CardContent>
        </Card>
      </Box>

      <Card sx={styles.logsCard}>
        <CardHeader title="Registration Log" sx={styles.cardHeader} />
        <CardContent sx={styles.cardContent}>
          <pre style={styles.jsonText as React.CSSProperties}>
            {JSON.stringify(twilioRegLog, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </Box>
  );
};

const styles = {
  container: {
    display: "flex",
    gap: "22px",
  },
  leftColumn: {
    width: "35%",
    display: "flex",
    flexDirection: "column",
    gap: "22px"
  },
  cardHeader: {
    marginBottom: "16px"
  },
  detailsCard: {
    display: "flex",
    flexDirection: "column"
  },
  sessionsCard: {
    display: "flex",
    flexDirection: "column"
  },
  logsCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column"
  },
  cardContent: {
    padding: "16px",
    height: "auto",
    overflow: "auto"
  },
  detailsSection: {
    marginBottom: "0px"
  },
  detailRow: {
    marginBottom: "15px"
  },
  buttonContainer: {
    marginTop: "10px",
    display: "flex",
    justifyContent: "center"
  },
  stepDescription: {
    display: 'inline'
  },
  mutedText: {
    color: 'text.secondary',
    display: 'inline'
  },
  boldText: {
    fontWeight: 'bold'
  },
  errorText: {
    color: 'red'
  },
  jsonText: {
    margin: 0,
    padding: 0,
    fontSize: '0.875rem',
    fontFamily: '"Roboto Mono", monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  }
};
